import { IsoTimestampSchema } from "./string-validation"
import { z } from 'zod'
import { OrderStatusSchema, OrderDisplayStatusSchema } from './order'

/**
 * Dashboard KPIs Schema
 */
export const DashboardKPIsSchema = z
  .object({
    stuckCases: z.number().int().optional(),
    failedPayments: z.number().int().optional(),
    pendingLabReviews: z.number().int().optional(),
    upcomingRefillLocks: z.number().int().optional(),
    openSupportTickets: z.number().int().optional(),
    casesCompletedToday: z.number().int().optional(),
  })
  .meta({ id: 'DashboardKPIs' })

export type DashboardKPIs = z.infer<typeof DashboardKPIsSchema>

/**
 * SLA Risk Level Schema
 */
export const SLARiskLevelSchema = z
  .enum(['low', 'medium', 'high', 'critical'])
  .meta({ id: 'SLARiskLevel' })

export type SLARiskLevel = z.infer<typeof SLARiskLevelSchema>

/**
 * SLA Risk Order Schema
 */
export const SLARiskOrderSchema = z
  .object({
    orderId: z.string(),
    patientName: z.string(),
    status: OrderStatusSchema,
    slaDeadline: IsoTimestampSchema,
    minutesRemaining: z.number().int(),
    riskLevel: SLARiskLevelSchema.optional(),
  })
  .meta({ id: 'SLARiskOrder' })

export type SLARiskOrder = z.infer<typeof SLARiskOrderSchema>


const DashboardCountSchema = z.number().int().nonnegative()
export const DashboardStageSchema = z.enum(["intake", "payment", "labs", "consultation", "fulfillment", "refills"])
export type DashboardStage = z.infer<typeof DashboardStageSchema>

export const DashboardQuerySchema = z.object({ page: z.coerce.number().int().min(1).max(100000).default(1) })

export const DashboardOverviewSchema = z.object({
  generatedAt: IsoTimestampSchema,
  totalOrders: DashboardCountSchema,
  inProgress: DashboardCountSchema,
  activeBlockers: DashboardCountSchema,
  onHold: DashboardCountSchema.nullable(),
  completed: DashboardCountSchema,
  stages: z.array(z.object({ stage: DashboardStageSchema, count: DashboardCountSchema })),
  outcomes: z.array(z.object({ status: OrderStatusSchema, count: DashboardCountSchema })),
  exceptions: z.object({
    failedPayments: DashboardCountSchema.nullable(),
    labFailures: DashboardCountSchema.nullable(),
    pharmacyFailures: DashboardCountSchema.nullable(),
  }),
  attention: z.object({
    total: DashboardCountSchema,
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    orders: z.array(z.object({
      id: z.uuid(),
      patientName: z.string().nullable(),
      status: OrderDisplayStatusSchema,
      blocked: z.boolean(),
      integrationFailed: z.boolean(),
      onHold: z.boolean(),
      updatedAt: IsoTimestampSchema,
    })),
  }),
}).meta({ id: "DashboardOverview" })
export type DashboardOverview = z.infer<typeof DashboardOverviewSchema>


export const StalledOrdersQuerySchema = z.object({
  hours: z.coerce.number().int().min(1).max(8760).default(24),
  page: z.coerce.number().int().min(1).max(100000).default(1),
})
export type StalledOrdersQuery = z.infer<typeof StalledOrdersQuerySchema>

export const StalledOrdersResponseSchema = z.object({
  generatedAt: IsoTimestampSchema,
  hours: z.number().int().min(1).max(8760),
  total: DashboardCountSchema,
  unknownHistoryCount: DashboardCountSchema,
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  orders: z.array(z.object({
    id: z.uuid(),
      patientName: z.string().nullable(),
    status: OrderStatusSchema,
    statusSince: IsoTimestampSchema,
    elapsedMinutes: DashboardCountSchema,
  })),
}).meta({ id: "StalledOrdersResponse" })
export type StalledOrdersResponse = z.infer<typeof StalledOrdersResponseSchema>
